import React, { useState, useEffect } from 'react';
import { Users, RefreshCw, Eye } from 'lucide-react';
import { authService } from '../services/auth';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getFullApiUrl } from '../config/api';

interface Segment {
  segmentId: string;
  name: string;
  type: string;
  enabled: boolean;
  userCount: number;
  lastUpdated: string;
  description?: string;
}

interface SegmentMember {
  userId: string;
  email?: string;
  addedAt: string;
  intentScore?: number;
}

const SegmentManagement: React.FC = () => {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null);
  const [segmentMembers, setSegmentMembers] = useState<SegmentMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [solutionFilter, setSolutionFilter] = useState<string>('all');

  useEffect(() => {
    loadSegments();
  }, [solutionFilter]);

  const loadSegments = async () => {
    setLoading(true);
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
      }
      
      const solutionParam = solutionFilter !== 'all' ? `?solutionId=${solutionFilter}` : '';
      const response = await fetch(getFullApiUrl(`/admin/segments${solutionParam}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSegments(data);
      } else {
        console.error('Failed to load segments:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to load segments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSegmentMembers = async (segmentId: string) => {
    setLoadingMembers(true);
    setSelectedSegment(segmentId);
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
      }
      
      const response = await fetch(
        getFullApiUrl(`/admin/segments/${segmentId}/members`),
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('Segment members loaded:', data);
        setSegmentMembers(data);
      } else {
        const errorText = await response.text();
        console.error('Failed to load segment members:', response.status, response.statusText, errorText);
        alert(`Failed to load segment members: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Failed to load segment members:', error);
      alert(`Error loading segment members: ${error}`);
    } finally {
      setLoadingMembers(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Solution Filter */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Solution</label>
        <select
          value={solutionFilter}
          onChange={(e) => setSolutionFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg"
        >
          <option value="all">All Solutions</option>
          <option value="aws-solution-finder">AWS Solution Finder (FasII)</option>
        </select>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Segments</h2>
        <button
          onClick={loadSegments}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Segment</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Users</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {segments.map((segment) => (
              <tr key={segment.segmentId} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{segment.name}</div>
                    {segment.description && (
                      <div className="text-sm text-gray-500">{segment.description}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {segment.type}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    segment.enabled 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {segment.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{segment.userCount || 0}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {segment.lastUpdated ? formatDate(segment.lastUpdated) : 'Never'}
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => loadSegmentMembers(segment.segmentId)}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <Eye className="w-4 h-4" />
                    View Users
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedSegment && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Segment Members: {segments.find(s => s.segmentId === selectedSegment)?.name}
            </h3>
            <button
              onClick={() => setSelectedSegment(null)}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Close
            </button>
          </div>

          {loadingMembers ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intent Score</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added At</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {segmentMembers.map((member) => (
                    <tr key={member.userId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{member.email || member.userId}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {member.intentScore !== undefined ? member.intentScore : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(member.addedAt)}</td>
                    </tr>
                  ))}
                  {segmentMembers.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500">
                        No users in this segment
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SegmentManagement;
